/**
 * Draws sold / reserved pixels (`pixels_all`) and cart overlays on the 3D map.
 */

import type { CartMap } from "../types";
import { socketClient } from "../api/socket";
import { config } from "../config";
import { loadGoogleMaps } from "./googleMapsLoader";
import {
  LAT_DISTANCE,
  calculateLngDistance,
  getLatLngFromBlockNumber,
  integerToBlock,
} from "./pixel";

const MAX_PIXELS = 2000;
const DEBOUNCE_MS = 150;
const OVERLAY_MARKER = "data-dwik-sold-overlay";

/** Mirrors latest `pixels_all` payload — used so hover cue matches sold overlays. */
const pixelsAllStatusById = new Map<number, number>();
const pixelsAllIndexListeners = new Set<() => void>();

function notifyPixelsAllIndexListeners(): void {
  for (const listener of [...pixelsAllIndexListeners]) {
    try {
      listener();
    } catch {
      /* avoid breaking map layer */
    }
  }
}

/**
 * Subscribe when the `pixels_all` index is rebuilt (sold / occupancy changed).
 * Used to refresh hover styling without moving the pointer.
 */
export function onPixelsAllIndexUpdated(listener: () => void): () => void {
  pixelsAllIndexListeners.add(listener);
  return () => pixelsAllIndexListeners.delete(listener);
}

/** Status from last `pixels_all` sync (`0` = free overlay convention in our UI); `undefined` if unknown. */
export function getSyncedPixelsAllStatus(pixelId: number): number | undefined {
  return pixelsAllStatusById.get(pixelId);
}

/** Matches legacy `<gmp-polygon-3d-interactive cart-pixel>` (config.fillColor + outline). */
const CART_MARKER = "data-dwik-cart-pixel";
const LEGACY_CART_FILL = "rgba(200, 200, 200, 0.35)";
const LEGACY_CART_STROKE = "rgba(0, 0, 0, 0.92)";
const LEGACY_CART_STROKE_WIDTH = 2;

export type Map3dElement = HTMLElement & {
  append: (...nodes: Node[]) => void;
  removeChild: (child: Node) => Node;
  center?: LatLngLite | object;
  range?: number;
  heading?: number;
  tilt?: number;
  fov?: number;
};

export type PolygonInteractiveLib = {
  PolyCtor: new (opts: Record<string, unknown>) => HTMLElement;
  /** Non-interactive polylines (e.g. dashed outlines); null if unavailable */
  PolylineCtor: ((new (
    opts: Record<string, unknown>,
  ) => HTMLElement) | null);
  altitudeMode: google.maps.maps3d.AltitudeMode;
};

let polygonInteractivePromise: Promise<PolygonInteractiveLib | null> | null =
  null;

/** Shared Maps3D Polygon3DInteractiveElement loader (sold + cart). */
export async function loadPolygonInteractiveLib(): Promise<
  PolygonInteractiveLib | null
> {
  if (!polygonInteractivePromise) {
    polygonInteractivePromise = (async () => {
      await loadGoogleMaps();
      type Maps3dLib = typeof google.maps.maps3d & {
        Polygon3DInteractiveElement?: new (
          opts: Record<string, unknown>,
        ) => HTMLElement;
        Polyline3DElement?: new (
          opts: Record<string, unknown>,
        ) => HTMLElement;
        AltitudeMode: typeof google.maps.maps3d.AltitudeMode;
      };
      const lib = (await google.maps.importLibrary(
        "maps3d",
      )) as unknown as Maps3dLib;
      const altitudeMode =
        lib.AltitudeMode?.RELATIVE_TO_MESH ??
        google.maps.maps3d.AltitudeMode.RELATIVE_TO_GROUND;
      const PolyCtor =
        lib.Polygon3DInteractiveElement ??
        (lib as unknown as { Polygon3DInteractiveElement?: new () => HTMLElement })
          .Polygon3DInteractiveElement ??
        null;
      if (!PolyCtor || typeof PolyCtor !== "function") return null;
      const rawPolylineCtor = lib.Polyline3DElement;
      const PolylineCtor =
        rawPolylineCtor && typeof rawPolylineCtor === "function"
          ? rawPolylineCtor
          : null;
      return { PolyCtor, PolylineCtor, altitudeMode };
    })();
  }
  return polygonInteractivePromise;
}

/** Match legacy websocket.js approximation when map.bounds is unavailable. */
export function syncBoundsApprox(
  center: { lat: number; lng: number },
  rangeMeters = 5000,
): {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
  range: number;
  max_pixels: number;
} {
  const latDelta = rangeMeters / 1000 / 111;
  const lngDelta =
    rangeMeters / 1000 / (111 * Math.cos((center.lat * Math.PI) / 180));
  return {
    minLat: center.lat - latDelta,
    maxLat: center.lat + latDelta,
    minLng: center.lng - lngDelta,
    maxLng: center.lng + lngDelta,
    range: rangeMeters,
    max_pixels: MAX_PIXELS,
  };
}

export function hexToRgba(hex: string, alpha = 0.6): string {
  const cleaned = hex.replace("#", "").trim();
  if (cleaned.length !== 6) return "rgba(0, 255, 255, 0.2)";
  const r = parseInt(cleaned.substring(0, 2), 16);
  const g = parseInt(cleaned.substring(2, 4), 16);
  const b = parseInt(cleaned.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const pixelOverlayAltitude =
  config.pixelMeshBaseAltitude + config.pixelOverlayAltitudeOffset;

export function blockPathForPixel(
  pixelInt: number,
  size = 1,
): google.maps.maps3d.LatLngAltitudeLiteral[] {
  const [x, y] = integerToBlock(pixelInt);
  return coordsToBoundary(x, y, pixelOverlayAltitude, size);
}

function coordsToBoundary(
  x: number,
  y: number,
  outerAltitude: number,
  size = 1,
): google.maps.maps3d.LatLngAltitudeLiteral[] {
  const [centerLat, centerLng] = getLatLngFromBlockNumber(x, y);

  let latDistance = LAT_DISTANCE * size;
  let lngDistance = calculateLngDistance(centerLat, 1) * size;
  if (centerLat < 0 || Object.is(centerLat, -0)) latDistance *= -1;
  if (centerLng < 0 || Object.is(centerLng, -0)) lngDistance *= -1;

  const swLat = centerLat;
  const swLng = centerLng;
  const nwLat = swLat + latDistance;
  const nwLng = swLng;
  const neLat = nwLat;
  const neLng = swLng + lngDistance;
  const seLat = swLat;
  const seLng = swLng + lngDistance;

  return [
    { lng: swLng, lat: swLat, altitude: outerAltitude },
    { lng: nwLng, lat: nwLat, altitude: outerAltitude },
    { lng: neLng, lat: neLat, altitude: outerAltitude },
    { lng: seLng, lat: seLat, altitude: outerAltitude },
    { lng: swLng, lat: swLat, altitude: outerAltitude },
  ];
}

type LatLngLite = { lat: number; lng: number };

export function readMapCenter(mapEl: Map3dElement): LatLngLite | null {
  const raw = mapEl.center;
  if (raw && typeof raw === "object") {
    if (
      typeof (raw as LatLngLite).lat === "number" &&
      typeof (raw as LatLngLite).lng === "number"
    ) {
      return { lat: (raw as LatLngLite).lat, lng: (raw as LatLngLite).lng };
    }
    const vals = Object.values(raw as Record<string, unknown>);
    const a = vals[0];
    const b = vals[1];
    if (typeof a === "number" && typeof b === "number") {
      return { lat: a, lng: b };
    }
  }
  return null;
}

export function readMapRange(mapEl: Map3dElement): number {
  return typeof mapEl.range === "number" && mapEl.range > 0
    ? mapEl.range
    : 5000;
}

export function readMapHeading(mapEl: Map3dElement): number {
  return typeof mapEl.heading === "number" && Number.isFinite(mapEl.heading)
    ? mapEl.heading
    : 0;
}

export function readMapTilt(mapEl: Map3dElement): number {
  return typeof mapEl.tilt === "number" && Number.isFinite(mapEl.tilt)
    ? mapEl.tilt
    : 20;
}

/** Vertical field of view in degrees (Maps 3D default 35). */
export function readMapFov(mapEl: Map3dElement): number {
  return typeof mapEl.fov === "number" && Number.isFinite(mapEl.fov)
    ? mapEl.fov
    : 35;
}

function updateSoldAndCartExtruded(mapEl: Map3dElement): void {
  const close = readMapRange(mapEl) < 30_000;
  const sel = `[${OVERLAY_MARKER}], [${CART_MARKER}]`;
  mapEl.querySelectorAll(sel).forEach((node) => {
    if (close) node.setAttribute("extruded", "");
    else node.removeAttribute("extruded");
  });
}

/** Redraw cart pixels: semi-transparent fill + dark stroke (`main.js#updateCartPixels`). */
export async function redrawCartPixelsOnMap(
  mapEl: Map3dElement,
  cart: CartMap,
): Promise<void> {
  const loaded = await loadPolygonInteractiveLib();
  if (!loaded || !mapEl.isConnected) return;

  const { PolyCtor, altitudeMode } = loaded;
  mapEl.querySelectorAll(`[${CART_MARKER}]`).forEach((node) => {
    try {
      mapEl.removeChild(node);
    } catch {
      /* detached */
    }
  });

  const extruded = readMapRange(mapEl) < 30_000;

  for (const key of Object.keys(cart)) {
    const px = cart[key];
    const pixelInt = px.pixel;
    const path = blockPathForPixel(pixelInt, 1);

    const gon = new PolyCtor({
      altitudeMode,
      fillColor: LEGACY_CART_FILL,
      path,
      strokeColor: LEGACY_CART_STROKE,
      strokeWidth: LEGACY_CART_STROKE_WIDTH,
      drawsOccludedSegments: true,
      extruded,
      geodesic: false,
      zIndex: 1,
    });

    gon.setAttribute("integer", String(pixelInt));
    gon.setAttribute("size", "1");
    gon.setAttribute("cart-pixel", String(pixelInt));
    gon.setAttribute(CART_MARKER, "1");
    mapEl.append(gon);
  }

  updateSoldAndCartExtruded(mapEl);
}

interface PixelRow {
  pixel: number;
  color?: string;
  status?: number;
  size?: number;
  username?: string;
}

function normalizeRow(entry: unknown): PixelRow | null {
  if (typeof entry === "number" && Number.isFinite(entry))
    return { pixel: entry };
  if (entry && typeof entry === "object" && "pixel" in entry) {
    const p = Number((entry as { pixel: unknown }).pixel);
    if (!Number.isFinite(p)) return null;
    const o = entry as Record<string, unknown>;
    return {
      pixel: p,
      color: typeof o.color === "string" ? o.color : undefined,
      status: typeof o.status === "number" ? o.status : undefined,
      size: typeof o.size === "number" ? o.size : 1,
      username: typeof o.username === "string" ? o.username : undefined,
    };
  }
  return null;
}

/** Subscribe sync + pixels_all; removes sold overlays on teardown. */
export function attachWorldSoldPixelsLayer(mapEl: Map3dElement): () => void {
  const soldById = new Map<number, HTMLElement>();

  let polygonLib: PolygonInteractiveLib | null = null;

  const drawOne = (
    pixelInt: number,
    rawColor: string | undefined,
    status: number,
    size: number,
    attrs: Record<string, string>,
  ) => {
    if (!polygonLib?.PolyCtor) return;

    const { PolyCtor, altitudeMode } = polygonLib;

    let fillColor =
      typeof rawColor === "string" && rawColor.trim()
        ? rawColor.trim()
        : "rgba(0, 255, 255, 0.25)";
    if (!fillColor.startsWith("rgba") && fillColor.startsWith("#")) {
      fillColor = hexToRgba(fillColor, 0.45);
    }

    const strokeColor: string = status === 1 ? "gray" : fillColor;
    const path = blockPathForPixel(pixelInt, size);
    const extruded = readMapRange(mapEl) < 30_000;

    const gon = new PolyCtor({
      altitudeMode,
      fillColor,
      path,
      strokeColor,
      strokeWidth: 1,
      drawsOccludedSegments: true,
      extruded,
      geodesic: false,
      zIndex: 0,
    });

    gon.setAttribute("integer", String(pixelInt));
    gon.setAttribute("size", String(size));
    gon.setAttribute(OVERLAY_MARKER, "1");
    for (const [k, v] of Object.entries(attrs)) {
      gon.setAttribute(k, v);
    }

    mapEl.append(gon);
    soldById.set(pixelInt, gon);
  };

  function applyPixelsAll(content: unknown): void {
    if (!polygonLib?.PolyCtor || !Array.isArray(content)) return;

    pixelsAllStatusById.clear();
    const newSet = new Set<number>();

    for (const raw of content) {
      const row = normalizeRow(raw);
      if (!row) continue;
      const status = typeof row.status === "number" ? row.status : 0;
      const size = row.size !== undefined && row.size >= 1 ? row.size : 1;
      const pixelInt = row.pixel;

      newSet.add(pixelInt);
      pixelsAllStatusById.set(pixelInt, status);

      const existing = soldById.get(pixelInt);
      if (existing) {
        const szAttr = Number(existing.getAttribute("size") ?? 1);
        const stAttr = Number(existing.getAttribute("status") ?? 0);
        const replace = size !== szAttr || status !== stAttr;
        if (!replace) continue;
        mapEl.removeChild(existing);
        soldById.delete(pixelInt);
      }

      drawOne(pixelInt, row.color, status, size, {
        status: String(status),
        ...(row.username ? { username: row.username } : {}),
      });
    }

    for (const [id, el] of [...soldById.entries()]) {
      if (!newSet.has(id)) {
        try {
          mapEl.removeChild(el);
        } catch {
          /* already detached */
        }
        soldById.delete(id);
      }
    }

    updateSoldAndCartExtruded(mapEl);
    notifyPixelsAllIndexListeners();
  }

  let debounceTimer: number | undefined;

  const requestSyncNow = (): void => {
    const ctr = readMapCenter(mapEl);
    if (!ctr) return;
    const payload = syncBoundsApprox(ctr, readMapRange(mapEl));
    socketClient.send("sync", payload);
  };

  const debouncedSync = (): void => {
    if (debounceTimer != null) window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(() => {
      debounceTimer = undefined;
      requestSyncNow();
      updateSoldAndCartExtruded(mapEl);
    }, DEBOUNCE_MS);
  };

  const onPixelsAll = (msg: { tag: string; content: unknown }) => {
    applyPixelsAll(msg.content);
  };

  let centerListener: EventListenerOrEventListenerObject | undefined;
  let rangeListener: EventListenerOrEventListenerObject | undefined;

  let cancelled = false;
  let offPixelsAll: () => void = () => {};

  void (async () => {
    polygonLib = await loadPolygonInteractiveLib();
    if (!polygonLib) {
      console.error(
        "Polygon3DInteractiveElement unavailable; sold pixels overlay disabled",
      );
    }
    if (cancelled) return;

    offPixelsAll = socketClient.on("pixels_all", onPixelsAll);

    await socketClient.connect();
    requestSyncNow();
    window.setTimeout(requestSyncNow, 400);
    window.setTimeout(requestSyncNow, 1200);

    centerListener = debouncedSync;
    rangeListener = debouncedSync;
    window.addEventListener(
      "gmp-centerchange",
      centerListener as EventListener,
      true,
    );
    window.addEventListener(
      "gmp-rangechange",
      rangeListener as EventListener,
      true,
    );
  })();

  return () => {
    cancelled = true;
    if (debounceTimer != null) window.clearTimeout(debounceTimer);
    try {
      offPixelsAll();
    } catch {
      /* noop */
    }
    if (centerListener) {
      window.removeEventListener(
        "gmp-centerchange",
        centerListener as EventListener,
        true,
      );
    }
    if (rangeListener) {
      window.removeEventListener(
        "gmp-rangechange",
        rangeListener as EventListener,
        true,
      );
    }
    for (const el of soldById.values()) {
      try {
        mapEl.removeChild(el);
      } catch {
        /* noop */
      }
    }
    soldById.clear();
    pixelsAllStatusById.clear();
    notifyPixelsAllIndexListeners();
  };
}
